import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import toast from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface ConversationData {
  id: number;
  session_id: string;
  messages: Message[];
  context: any;
  started_at: string;
  ended_at: string | null;
  completed: boolean;
  message_count: number;
  duration: number | null;
}

interface LeadInfo {
  id: number;
  email: string;
  parent_name?: string;
  children?: any[];
  created_at: string;
}

interface ConversationViewerProps {
  leadId: number;
  onClose: () => void;
}

const ConversationViewer: React.FC<ConversationViewerProps> = ({ leadId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [leadInfo, setLeadInfo] = useState<LeadInfo | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch lead details which includes conversations
      const response = await authService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_URL}/api/leads/${leadId}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch lead details');
      }

      const data = await response.json();
      
      // Format conversations to match expected interface
      const formattedConversations = data.conversations.map((conv: any) => ({
        id: conv.id,
        session_id: conv.session_id,
        messages: conv.messages || [],
        context: conv.context ? (typeof conv.context === 'string' ? JSON.parse(conv.context) : conv.context) : {},
        started_at: conv.started_at,
        ended_at: conv.ended_at,
        completed: conv.completed,
        message_count: conv.messages ? conv.messages.length : 0,
        duration: conv.ended_at 
          ? Math.round((new Date(conv.ended_at).getTime() - new Date(conv.started_at).getTime()) / 1000 / 60) 
          : null
      }));

      setConversations(formattedConversations);
      setLeadInfo({
        id: data.lead.id,
        email: data.lead.email,
        parent_name: data.lead.parent_name,
        children: data.lead.children,
        created_at: data.lead.created_at
      });
      setSelectedConversation(0);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const highlightText = (text: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <mark key={index} className="bg-yellow-200">{part}</mark> : 
        part
    );
  };

  const currentConversation = conversations[selectedConversation];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Conversation History</h2>
            <p className="text-sm text-gray-600 mt-1">
              {leadInfo?.parent_name || 'Unknown'} - {leadInfo?.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Conversation List */}
          <div className="w-80 border-r bg-gray-50 flex flex-col">
            <div className="p-4 border-b bg-white">
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No conversations found
                </div>
              ) : (
                conversations.map((conv, index) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(index)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      selectedConversation === index 
                        ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        Session #{index + 1}
                      </span>
                      {conv.completed && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          Completed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {formatDate(conv.started_at)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {conv.message_count} messages
                      {conv.duration && ` • ${conv.duration} min`}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Lead Context Panel */}
            {leadInfo?.children && leadInfo.children.length > 0 && (
              <div className="p-4 border-t bg-white">
                <h4 className="font-semibold text-gray-900 mb-2">Children Information</h4>
                {leadInfo.children.map((child: any, index: number) => (
                  <div key={index} className="mb-2 text-sm">
                    <div className="font-medium text-gray-700">
                      {child.name} {child.age && `(${child.age} years)`}
                    </div>
                    {child.special_needs && child.special_needs.length > 0 && (
                      <div className="text-gray-600">
                        Needs: {child.special_needs.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main Chat View */}
          <div className="flex-1 flex flex-col bg-white">
            {currentConversation ? (
              <>
                {/* Conversation Header */}
                <div className="px-6 py-3 border-b bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Started: {formatDate(currentConversation.started_at)}
                      </span>
                      {currentConversation.ended_at && (
                        <span className="text-sm text-gray-600 ml-4">
                          Ended: {formatDate(currentConversation.ended_at)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Session ID: {currentConversation.session_id.substring(0, 8)}...
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {currentConversation.messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-2xl rounded-lg px-4 py-3 ${
                          message.role === 'user'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">
                          {highlightText(message.content)}
                        </div>
                        <div 
                          className={`text-xs mt-1 ${
                            message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                          }`}
                        >
                          {formatTimestamp(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Context Information */}
                {currentConversation.context && Object.keys(currentConversation.context).length > 0 && (
                  <div className="px-6 py-4 border-t bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Extracted Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {currentConversation.context.parentName && (
                        <div>
                          <span className="text-gray-600">Parent:</span>{' '}
                          <span className="font-medium">{currentConversation.context.parentName}</span>
                        </div>
                      )}
                      {currentConversation.context.email && (
                        <div>
                          <span className="text-gray-600">Email:</span>{' '}
                          <span className="font-medium">{currentConversation.context.email}</span>
                        </div>
                      )}
                      {currentConversation.context.children && currentConversation.context.children.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Children:</span>{' '}
                          <span className="font-medium">
                            {currentConversation.context.children.map((c: any) => c.name).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                Select a conversation to view
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationViewer;